/**
 * "Frequently Bought Together" in the cart drawer, powered by Shopify's product recommendations.
 *
 * snippets/cart-drawer.liquid server-renders <mdj-cart-recs> with the hand-picked list (Theme settings →
 * cart_products), already filtered for stock and cart contents. This element then asks Shopify for
 * recommendations for the most recently added cart products and shows those first, keeping the
 * hand-picked products as the fill-in:
 *   1. intent=complementary — pairings set up in Shopify's Search & Discovery app (weighted highest)
 *   2. intent=related       — Shopify's automatic picks, learned from order history and product similarity
 * Products suggested for several cart items, or ranked higher, come first. Sold-out products, anything
 * already in the cart and duplicates are skipped. Cards come from sections/cart-recommendations.liquid,
 * so they match the hand-picked cards exactly.
 */
(() => {
  if (customElements.get('mdj-cart-recs')) return;

  const SECTION_ID = 'cart-recommendations';
  const MAX_ANCHORS = 2; // most recently added cart products to base suggestions on
  const INTENTS = [
    { intent: 'complementary', weight: 2 },
    { intent: 'related', weight: 1 },
  ];
  const CACHE_TTL = 10 * 60 * 1000; // re-ask Shopify at most every 10 minutes per product
  const pending = new Map();

  function loadCards(url, productId, intent) {
    const themeId = (window.Shopify && window.Shopify.theme && window.Shopify.theme.id) || '';
    const key = `mdj-recs:v1:${themeId}:${intent}:${productId}`;
    if (!pending.has(key)) {
      let cachedHtml = null;
      try {
        const stored = JSON.parse(sessionStorage.getItem(key) || 'null');
        if (stored && Date.now() - stored.t < CACHE_TTL) cachedHtml = stored.html;
      } catch (e) {}

      const html = cachedHtml !== null
        ? Promise.resolve(cachedHtml)
        : fetch(`${url}?product_id=${encodeURIComponent(productId)}&limit=10&intent=${intent}&section_id=${SECTION_ID}`)
            .then((res) => {
              if (!res.ok) return '';
              return res.text().then((text) => {
                try { sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), html: text })); } catch (e) {}
                return text;
              });
            })
            .catch(() => '');

      pending.set(key, html.then((text) =>
        Array.from(new DOMParser().parseFromString(text, 'text/html').querySelectorAll('.product[data-product-id]'))
      ));
    }
    return pending.get(key);
  }

  class MdjCartRecs extends HTMLElement {
    connectedCallback() {
      if (this.dataset.state) return;
      this.dataset.state = 'loading';
      this.update()
        .catch(() => {})
        .finally(() => {
          this.dataset.state = 'done';
          // Hook every card's ADD button up to the theme's add-to-cart handler (layout/theme.liquid).
          if (typeof window.mdjBindAddToCart === 'function') window.mdjBindAddToCart(this);
        });
    }

    async update() {
      const list = this.querySelector('.mdj-cart-recs__list');
      const url = this.dataset.url;
      const inCart = (this.dataset.cartProducts || '').split(',').filter(Boolean);
      const limit = parseInt(this.dataset.limit, 10) || 4;
      if (!list || !url || !inCart.length) return;

      const exclude = new Set(inCart);
      const scores = new Map();
      await Promise.all(
        inCart.slice(0, MAX_ANCHORS).flatMap((productId) =>
          INTENTS.map(({ intent, weight }) =>
            loadCards(url, productId, intent).then((cards) => {
              cards.forEach((card, rank) => {
                const id = card.dataset.productId;
                if (exclude.has(id)) return;
                const entry = scores.get(id) || { card, score: 0 };
                entry.score += weight / (rank + 1);
                scores.set(id, entry);
              });
            })
          )
        )
      );

      const recommended = Array.from(scores.values())
        .sort((a, b) => b.score - a.score)
        .map((entry) => document.importNode(entry.card, true));
      const handPicked = Array.from(list.querySelectorAll('.product[data-product-id]'));

      const cards = [];
      const seen = new Set();
      for (const card of recommended.concat(handPicked)) {
        const id = card.dataset.productId;
        if (seen.has(id) || exclude.has(id)) continue;
        seen.add(id);
        cards.push(card);
        if (cards.length >= limit) break;
      }
      if (!cards.length) return;

      list.replaceChildren(...cards);
      this.hidden = false;
    }
  }

  customElements.define('mdj-cart-recs', MdjCartRecs);
})();
